import { ChevronDown, Download, Loader2, X } from 'lucide-react';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { AdminUserBulkAction } from '@/client/types/admin/users-orgs';
import { BULK_ACTION_META, MAX_BULK_USERS, MAX_EXPORT_USERS } from './bulk-actions';

export const BulkActionBar: React.FC<{
  count: number;
  total: number;
  allMatching: boolean;
  /** The whole visible page is ticked, so "select all N matching" is on offer. */
  pageFullySelected: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
  onAction: (action: AdminUserBulkAction) => void;
  onExport: () => void;
  exporting: boolean;
}> = ({ count, total, allMatching, pageFullySelected, onSelectAllMatching, onClear, onAction, onExport, exporting }) => {
  const canAssign = useGate(PERMISSIONS.user.roleAssign);
  const canRevoke = useGate(PERMISSIONS.user.roleRevoke);
  const canEdit = useGate(PERMISSIONS.user.edit);
  const tooManyForBulk = count > MAX_BULK_USERS;
  const tooManyForExport = count > MAX_EXPORT_USERS;
  const capHint = `Bulk actions take up to ${MAX_BULK_USERS.toLocaleString()} users — narrow the filters.`;

  const action = (key: AdminUserBulkAction, variant: 'outline' | 'destructive' = 'outline') => (
    <PermissionButton
      code={BULK_ACTION_META[key].code}
      size="sm"
      variant={variant}
      disabled={tooManyForBulk}
      title={tooManyForBulk ? capHint : undefined}
      onClick={() => onAction(key)}
    >
      {BULK_ACTION_META[key].label}
    </PermissionButton>
  );

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="flex flex-wrap items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2.5"
    >
      <span className="text-sm font-medium text-blue-900">
        {count.toLocaleString()} selected{allMatching ? ' (all matching)' : ''}
      </span>
      {!allMatching && pageFullySelected && total > count && (
        <button type="button" className="text-sm font-medium text-blue-700 underline" onClick={onSelectAllMatching}>
          Select all {total.toLocaleString()} matching users
        </button>
      )}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={tooManyForBulk || (!canAssign.allowed && !canRevoke.allowed)}
              title={tooManyForBulk ? capHint : undefined}
            >
              Change role <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={!canAssign.allowed} title={canAssign.allowed ? undefined : canAssign.reason} onSelect={() => onAction('add_role')}>
              Add a role…
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!canRevoke.allowed} title={canRevoke.allowed ? undefined : canRevoke.reason} onSelect={() => onAction('remove_role')}>
              Remove a role…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={tooManyForBulk || !canEdit.allowed}
              title={tooManyForBulk ? capHint : canEdit.allowed ? undefined : canEdit.reason}
            >
              Test status <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onAction('mark_test')}>Mark as test…</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onAction('mark_live')}>Mark as live…</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {action('unsuspend')}
        {action('suspend', 'destructive')}
        {action('restore')}
        {action('delete', 'destructive')}
        <PermissionButton
          code={PERMISSIONS.user.view}
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={exporting || tooManyForExport}
          title={tooManyForExport ? `Exports take up to ${MAX_EXPORT_USERS.toLocaleString()} users.` : undefined}
          onClick={onExport}
        >
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export CSV
        </PermissionButton>
        <Button size="sm" variant="ghost" className="gap-1 text-blue-900" onClick={onClear}>
          <X className="h-3.5 w-3.5" /> Clear
        </Button>
      </div>
    </div>
  );
};
